import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";

import { useApp } from "../../App/useApp";
import { useDraftsStore } from "../store/useDraftsStore";
import { useConversationStore } from "../store/useConversationStore";
import { useUserStore } from "../store/useUsersStore";
import {
    IConversationNavOptions,
    IMessagePlaceholder
} from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import {
    formatDisplayMessage,
    formatDateToLocalBasedOnDayMode,
    formatConversationTitle
} from "../../../common/vue-helpers/modifiers/format-message";
import { markdownToHtml } from "../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../common/vue-helpers/modifiers/value-modifier";
import { ConversationSortEnum, ConversationTypeEnum } from "../../../enums/enums";
import eventBus from "../../../utilities/eventBus";
import { MessageService } from "../../../services/message-service";
import { ToastrService } from "../../../services/toastr.service";
import { debounce } from "../../../common/vue-helpers/debounce";
import { AnalyticsCategory, AnalyticsService } from "../../../services/analytics.service";

import AvaInitials from "../components/AvaInitials/AvaInitials.vue";

export default defineComponent({
    components: {
        AvaInitials
    },

    props: {
        newConversation: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        pageNumber: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _toastrService: null as ToastrService,
            _messageService: null as MessageService,
            selectedConversationId: null as string,
            messageNavOptions: [],
            term: "" as string,
            openSearch: false as boolean,
            showSortedConversation: false as boolean,
            userOs: "",
            sortText: "Unread",
            sortBy: ConversationSortEnum.Recent,
            taggedUsersList: [] as IMessagePlaceholder[],
            highlightTaggedUser: false as boolean,
            searchTermDebounce: null,
            formatConversationTitle,
            formatDateToLocalBasedOnDayMode,
            markdownToHtml,
            sanitizeMarkup,
            formatDisplayMessage,
            ConversationTypeEnum,
            ConversationSortEnum
        };
    },

    computed: {
        ...mapState(useApp, ["userSubId"]),
        ...mapState(useConversationStore, [
            "activeConversationId",
            "getAllConversationsList",
            "orderedConvIds",
            "showFilteredConvs"
        ]),
        ...mapState(useUserStore, ["getUser"]),
        ...mapState(useDraftsStore, ["messageDrafts", "getAllConversationDrafts"]),

        selectedConversationClass() {
            const conversationId = (this.$route.params?.id as string) || this.allMessageNavOptions[0].id;
            return (index: number) => {
                return {
                    active: !this.newConversation && conversationId === this.allMessageNavOptions[index].id
                };
            };
        },
        allMessageNavOptions(): IConversationNavOptions[] {
            if (this.showFilteredConvs) {
                return [...this.getAllConversationsList];
            } else {
                const drafts = this.getAllConversationDrafts;
                return [...drafts.reverse(), ...this.getAllConversationsList];
            }
        }
    },

    watch: {
        term: function (newValue, oldValue) {
            if (newValue != oldValue) {
                this.searchTermDebounce();
            }
        },

        orderedConvIds: {
            handler() {
                this.messageNavOptions = this.getAllConversationsList;
            },
            immediate: true,
            deep: true
        }
    },

    emits: ["termChanged", "sortByChanged"],

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this.searchTermDebounce = debounce(this.termChanged, 500);
        this._messageService = Container.instance.get(MessageService);
        this._toastrService = Container.instance.get(ToastrService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    methods: {
        ...mapActions(useConversationStore, ["setActiveConversationId", "clearFilteredData"]),

        async handleMenuSelection(conv: IConversationNavOptions) {
            if (this.newConversation) {
                eventBus.emit("updateNewConversation", false);
            }
            this.selectedConversationId = conv.id;
            this.setActiveConversationId(conv.id);
        },

        navigateToNewConversation() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.AxxessConnect,
                action: "Connect New Conversation"
            });
            this.setActiveConversationId("newConversation");
            eventBus.emit("updateNewConversation", true);
        },

        cancelNewConversation() {
            eventBus.emit("updateNewConversation", false);
            if (!this.selectedConversationId) {
                this.selectedConversationId = this.orderedConvIds[0];
            }
            this.setActiveConversationId(this.selectedConversationId, false);
        },

        termChanged() {
            this.$emit("termChanged", this.term);
        },

        toggleSearchBox(value: boolean) {
            this.openSearch = value;
            if (!this.openSearch) {
                this.clearFilteredData();
                this.term = "";
            }
        },

        toggleSort(oldSortBy: ConversationSortEnum) {
            if (oldSortBy === ConversationSortEnum.Recent) {
                this.sortBy = ConversationSortEnum.Unread;
                this.sortText = "Recent";
            } else {
                this.sortBy = ConversationSortEnum.Recent;
                this.sortText = "Unread";
            }
            this.$emit("sortByChanged", this.sortBy);
        },

        async markAllAsRead() {
            try {
                await this._messageService.readAllMessages();
                this._toastrService.success({
                    title: `Success`,
                    message: `Marked all as read.`
                });
            } catch (e) {
                console.error(e);
            }
        }
    }
});
